import React from "react";

export default function AjpRouteSeoContent({ page, compact = false }) {
  if (!page?.routeContext) return null;
  const context = page.routeContext;
  const cardStyle = {
    padding: compact ? 16 : 22,
    border: "1px solid #dbe7f7",
    borderRadius: compact ? 14 : 18,
    background: "#fff",
    boxShadow: "0 10px 28px rgba(15,47,99,.06)",
  };
  const outbound = page.direction !== "return";
  const airportLabel = page.airportName || "the airport";
  const cityLabel = page.city || "your destination";
  const calculatedComparison = [
    {
      title: "Rail times, duration and fare",
      body: `See the available rail itinerary between ${cityLabel} and ${airportLabel}, including departure and arrival times, total duration, changes and an estimated fare. If a train arrives after an outbound flight, it remains visible but is clearly marked as not viable.`,
    },
    {
      title: "Taxi time and estimated fare",
      body: `Compare a door-to-terminal taxi journey with its estimated travel time, suggested ${outbound ? "pickup and leave-home time" : "terminal pickup time"}, route and fare for the address entered.`,
    },
    {
      title: "Minibus journey and group quote",
      body: `For groups, compare the same airport journey timing and request a tailored minibus quote. Passenger, luggage, flight and terminal details can be carried into the quote enquiry.`,
    },
    {
      title: outbound ? "Self-drive time, parking and fuel" : "Onward driving time",
      body: outbound
        ? `Review driving duration, the time to leave home, airport parking and return-fuel estimates, including terminal transfer time where applicable.`
        : `Review the estimated driving time from ${airportLabel} after the flight and terminal-exit allowance.`,
    },
  ];

  return (
    <section aria-labelledby="ajp-route-guide-title" style={{ padding: compact ? "12px 14px 16px" : "18px 16px 26px" }}>
      <div style={{ maxWidth: 1440, margin: "0 auto" }}>
        <p style={{ margin: "0 0 5px", color: "#176b50", fontSize: 12, fontWeight: 900, textTransform: "uppercase", letterSpacing: ".05em" }}>Route guide</p>
        <h2 id="ajp-route-guide-title" style={{ margin: "0 0 8px", color: "#08245f", fontSize: compact ? 24 : 34 }}>{page.heading}</h2>
        <p style={{ margin: "0 0 18px", maxWidth: 850, color: "#526783", lineHeight: 1.6 }}>{page.description}</p>

        <div style={{ display: "grid", gridTemplateColumns: compact ? "1fr" : "repeat(2,minmax(0,1fr))", gap: 14 }}>
          <article style={cardStyle}><h3 style={{ marginTop: 0, color: "#0b2b64" }}>Road journey</h3><p style={{ color: "#526783", lineHeight: 1.55 }}><strong>{context.roads.join(" → ")}</strong>. {context.typicalRoadTime}</p></article>
          <article style={cardStyle}><h3 style={{ marginTop: 0, color: "#0b2b64" }}>Rail journey</h3><p style={{ color: "#526783", lineHeight: 1.55 }}>{context.railSummary}</p><p style={{ marginBottom: 0, color: "#176b50", fontWeight: 800 }}>{context.stations.join(" → ")}</p></article>
          <article style={cardStyle}><h3 style={{ marginTop: 0, color: "#0b2b64" }}>Taxi and minibus</h3><p style={{ color: "#526783", lineHeight: 1.55 }}>Compare a door-to-terminal taxi with a group minibus enquiry using the exact address, passenger needs and selected flight time.</p></article>
          <article style={cardStyle}><h3 style={{ marginTop: 0, color: "#0b2b64" }}>When to travel</h3><p style={{ color: "#526783", lineHeight: 1.55 }}>{context.whenToLeave}</p></article>
        </div>

        <div style={{ marginTop: 18, ...cardStyle }}>
          <h2 style={{ marginTop: 0, color: "#08245f", fontSize: compact ? 22 : 28 }}>What your live journey plan compares</h2>
          <p style={{ color: "#526783", lineHeight: 1.6 }}>Enter an address and flight, or the flight {outbound ? "departure" : "arrival"} time manually. The planner calculates the following details for this specific journey rather than publishing a fixed generic price or timetable.</p>
          <div style={{ display: "grid", gridTemplateColumns: compact ? "1fr" : "repeat(2,minmax(0,1fr))", gap: 12 }}>
            {calculatedComparison.map((item) => (
              <article key={item.title} style={{ padding: "14px 16px", border: "1px solid #dbe7f7", borderRadius: 12, background: "#f8fbff" }}>
                <h3 style={{ margin: "0 0 6px", color: "#0b2b64", fontSize: 17 }}>{item.title}</h3>
                <p style={{ margin: 0, color: "#526783", lineHeight: 1.55 }}>{item.body}</p>
              </article>
            ))}
          </div>
        </div>

        <div style={{ marginTop: 18, ...cardStyle }}>
          <h2 style={{ marginTop: 0, color: "#08245f", fontSize: compact ? 22 : 28 }}>Flight, terminal and live airport checks</h2>
          <p style={{ color: "#526783", lineHeight: 1.6 }}>When flight data is available, the plan checks the selected flight and terminal and works from its scheduled time. It also presents the latest prepared {airportLabel} status, including relevant flight disruption, terminal conditions, weather, road and rail information. Security or immigration queue information is shown where a reliable airport feed is available.</p>
          <p style={{ marginBottom: 0, color: "#526783", lineHeight: 1.6 }}>Current conditions are a snapshot. Journey alerts can monitor the saved journey and notify the traveller when a monitored condition changes closer to the day of travel.</p>
        </div>

        <div style={{ marginTop: 18, ...cardStyle, borderColor: "#f0cf72", background: "#fffaf0" }}>
          <h2 style={{ marginTop: 0, color: "#08245f", fontSize: compact ? 22 : 28 }}>Travelling as a group?</h2>
          <p style={{ marginBottom: 0, color: "#526783", lineHeight: 1.6 }}>Use the minibus option to check the journey timing and request a group quote for the exact route. The enquiry can include passenger numbers, bags, flight number, airport terminal and requested travel time—useful for families, business groups and larger parties.</p>
        </div>

        <div style={{ marginTop: 18, ...cardStyle }}>
          <h2 style={{ marginTop: 0, color: "#08245f", fontSize: compact ? 22 : 28 }}>Common questions</h2>
          <div style={{ display: "grid", gap: 10 }}>
            {page.faqs.map((faq) => (
              <details key={faq.question} style={{ padding: "12px 14px", border: "1px solid #dbe7f7", borderRadius: 12, background: "#f8fbff" }}>
                <summary style={{ color: "#0b2b64", fontWeight: 850, cursor: "pointer" }}>{faq.question}</summary>
                <p style={{ margin: "10px 0 0", color: "#526783", lineHeight: 1.55 }}>{faq.answer}</p>
              </details>
            ))}
          </div>
        </div>

        <a href={page.reversePath} style={{ display: "inline-flex", marginTop: 18, color: "#075bea", fontWeight: 850 }}>{page.reverseLabel} →</a>
      </div>
    </section>
  );
}
